import type { Availability } from '@acs/shared';
import type { TranslationKey } from '../../i18n/index.js';

/**
 * The booking-notice choices an owner is offered (doc 84) — how soon before an
 * appointment a caller can still book it. Each choice is a pair of the two
 * stored settings, so the select can never write a combination the assistant
 * would describe differently from its label: a duration (`minNoticeMin`) OR
 * calendar days (`minNoticeDays`), never both.
 *
 * 24 hours and "from the next day" are both offered on purpose: at 15:00 the
 * first means tomorrow from 15:00, the second tomorrow from opening.
 */
export interface NoticeChoice {
  key: string;
  minNoticeMin: number;
  minNoticeDays: number;
  labelKey: TranslationKey;
}

export const NOTICE_CHOICES: readonly NoticeChoice[] = [
  { key: 'm0', minNoticeMin: 0, minNoticeDays: 0, labelKey: 'settings.notice.any' },
  { key: 'm15', minNoticeMin: 15, minNoticeDays: 0, labelKey: 'settings.notice.m15' },
  { key: 'm30', minNoticeMin: 30, minNoticeDays: 0, labelKey: 'settings.notice.m30' },
  { key: 'm45', minNoticeMin: 45, minNoticeDays: 0, labelKey: 'settings.notice.m45' },
  { key: 'm60', minNoticeMin: 60, minNoticeDays: 0, labelKey: 'settings.notice.h1' },
  { key: 'm120', minNoticeMin: 120, minNoticeDays: 0, labelKey: 'settings.notice.h2' },
  { key: 'm180', minNoticeMin: 180, minNoticeDays: 0, labelKey: 'settings.notice.h3' },
  { key: 'm240', minNoticeMin: 240, minNoticeDays: 0, labelKey: 'settings.notice.h4' },
  { key: 'm360', minNoticeMin: 360, minNoticeDays: 0, labelKey: 'settings.notice.h6' },
  { key: 'm720', minNoticeMin: 720, minNoticeDays: 0, labelKey: 'settings.notice.h12' },
  { key: 'm1440', minNoticeMin: 1440, minNoticeDays: 0, labelKey: 'settings.notice.h24' },
  { key: 'd1', minNoticeMin: 0, minNoticeDays: 1, labelKey: 'settings.notice.d1' },
  { key: 'd2', minNoticeMin: 0, minNoticeDays: 2, labelKey: 'settings.notice.d2' },
];

export type NoticeSetting = Pick<Availability, 'minNoticeMin' | 'minNoticeDays'>;

/** The select's value for a stored setting: a choice's key, or `custom` for a
 * pair no choice matches (set before the control existed, or by hand). */
export function noticeKey(s: NoticeSetting): string {
  return NOTICE_CHOICES.find((c) => c.minNoticeMin === s.minNoticeMin && c.minNoticeDays === s.minNoticeDays)?.key ?? 'custom';
}

/** The stored pair for a select value; `custom` keeps what is stored. */
export function noticeSetting(key: string, current: NoticeSetting): NoticeSetting {
  const c = NOTICE_CHOICES.find((x) => x.key === key);
  return c ? { minNoticeMin: c.minNoticeMin, minNoticeDays: c.minNoticeDays } : current;
}
